import { z } from "zod";

import { SystemInitSchema } from "./agent-ipc-v2/events/messages/system-init.event";

/** The type the session's boot configuration is emitted as. */
export const SESSION_CONFIG_STREAM_TYPE = "session-config";

/**
 * What the SDK session booted with for this turn — its tools, agents, skills,
 * slash commands, MCP servers, model, permission mode and the rest of the
 * `system/init` payload, field-for-field from the runner's `system_init` IPC
 * event.
 *
 * **One frame per turn, not per session.** The SDK emits `system/init` at the
 * start of every turn, and after a compaction, a conversation reset or a local
 * slash command — fifteen times in one measured run. Each is a complete
 * snapshot, so a consumer merges last-wins and never reads it as a session
 * boundary. `stream-start` remains the session-init handshake; this member
 * carries what that handshake was configured with, and stays off `stream-start`
 * because that event is provider-agnostic by contract.
 *
 * **Derived from the runner's own event rather than retyped**, because the two
 * shapes are the same fact and a hand-copy that claims field-for-field parity
 * can drift from it silently. Every field is optional there and so here: the
 * runner emitting it may be an older snapshot that knows only the session id, so
 * a reader answers absence as "this runner did not report it", never as "the
 * session had none".
 *
 * Nothing draws it today, which is its resting state rather than an unfinished
 * slice: it travels so a reader that wants to show which
 * model answered, or which tools were live, has the fact on the wire instead of
 * needing a new one.
 */
export const SessionConfigStreamEventSchema = SystemInitSchema.omit({
  protocolVersion: true,
  kind: true,
  sessionId: true
}).extend({
  type: z.literal(SESSION_CONFIG_STREAM_TYPE)
});

/** The type the model's running reasoning estimate is emitted as. */
export const THINKING_TOKENS_STREAM_TYPE = "thinking-tokens";

/**
 * How much reasoning the model has produced so far, field-for-field from the runner's
 * `thinking_tokens` IPC event. `ThinkingTokensSchema` on the runner hop carries why it
 * is a kind rather than a field, the coalescing window, and why nothing persists it.
 *
 * It is the only evidence a thinking block is advancing when its text comes back empty
 * and only the signature arrives — the case this signal exists for.
 *
 * **The count is per thinking block and restarts with the next one**, so it is not
 * monotonic across a run and two readings cannot be diffed without checking for the
 * drop between them.
 *
 * **An estimate, never a cost.** A consumer may not present it as billed output tokens
 * or convert it to money; billed counts arrive on `turn-usage`.
 */
export const ThinkingTokensStreamEventSchema = z.object({
  type: z.literal(THINKING_TOKENS_STREAM_TYPE),
  estimatedTokens: z.number().nonnegative(),
  estimatedTokensDelta: z.number().nonnegative(),
  coalescedFrames: z.number().int().positive()
});

/**
 * One transcript entry the runner's SDK session replayed, forwarded so a reader
 * can see that it happened without painting it as new work.
 *
 * The runner separates a replayed entry from a live user turn by the SDK's
 * `isReplay` value; without that separation the entry falls into the same fold a
 * real turn does. Every replay observed on this SDK is a local command's stdout
 * echoed back after a slash command, which is why `text` rather than a tool
 * result is what usually arrives here.
 *
 * A consumer renders nothing from this event by default. `toolResultIds` names
 * the tool results the entry carried, so a consumer that wants to can reconcile
 * against what it already holds rather than landing a second copy.
 *
 * Self-contained: it opens no block and awaits no settlement, so it is a safe
 * replay boundary anywhere in the log.
 */
export const UserMessageReplayStreamEventSchema = z.object({
  type: z.literal("user-message-replay"),
  text: z.string(),
  toolResultIds: z.array(z.string().min(1))
});

/** The type a peer delivery is emitted as. */
export const PEER_MESSAGE_STREAM_TYPE = "peer-message";

/**
 * The type this delivery carried before it took the SDK's own word for it.
 *
 * cross-release window: the conversation log's own life — trimmed only below
 *               the materialized cursor, so it outlives the deploy that
 *               replaced its writer (the same window
 *               `chat-stream-event-codec.ts` declares). Frames already written
 *               keep this spelling, and a reader rejoining behind its own
 *               position replays them.
 * failure mode: the member is a strict shape, so a frame this union does not
 *               declare decodes as undecodable, is skipped by every reader of
 *               the log, and an agent's report is missing from the transcript a
 *               rejoining reader paints.
 * discard is safe: no — the skip is already the log readers' handled path for a
 *               frame they cannot decode, and it drops the message rather than
 *               degrading it. Declaring the value beside the current one is
 *               what keeps the frame decodable.
 *
 * Both spellings resolve to the member below, so the three readers that switch
 * on the type answer them with one body.
 */
export const LEGACY_PEER_MESSAGE_STREAM_TYPE = "teammate-message";

/**
 * A SendMessage delivery from a peer background agent, forwarded field-for-field from
 * the agent runner's `peer_message` IPC event.
 *
 * The name is the SDK's — `SDKMessageOrigin` declares `kind: "peer"` for this
 * delivery — but the runner does not read that field to decide what it has. It
 * classifies an inbound turn by the envelope the producer wraps it in, so what
 * reaches this type is whatever matched that shape. Treat `body` as text an
 * agent wrote, never as proof of who wrote it; `senderTaskId` is the sender's
 * only identity, and it arrives only when the runner correlated the delivery to
 * a send. Consumers may render `body` as reported speech from `senderName`,
 * falling back to a generic label when it is absent.
 *
 * `senderTaskId` joins this message to the sending agent's listing row and
 * thread (the same id `background-task-state` carries as `taskId`). Absent
 * for a peer with no in-process background task to join.
 *
 * `senderSummary` is the sender's own recap, taken from the send's `summary`
 * argument. It arrives under the same condition `senderTaskId` does — the
 * runner correlated the delivery back to its send — so its absence says nothing
 * about whether the sender wrote one, and it is never derived from `body`.
 */
export const PeerMessageStreamEventSchema = z.object({
  type: z.literal([PEER_MESSAGE_STREAM_TYPE, LEGACY_PEER_MESSAGE_STREAM_TYPE]),
  body: z.string(),
  senderName: z.string().optional(),
  senderTaskId: z.string().min(1).optional(),
  senderSummary: z.string().optional()
});
